import * as React from 'react';
import type { IOption, ISelectValueRenderState, SelectOptionValue } from '../types';
import { CaptionTypography, ChevronDown } from '@components/atoms';
import { cn } from '@utils';

/**
 * Interface for the props of the select trigger component.
 * @template TValue - The type of the value of the option.
 * @returns {ISelectTriggerProps} - The props object.
 */
export interface ISelectTriggerProps<
	TValue extends SelectOptionValue = SelectOptionValue,
	TMetadata = unknown
> extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	error?: string;
	open?: boolean;
	placeholder: string;
	renderValue?: (options: ReadonlyArray<IOption<TValue, TMetadata>>, state: ISelectValueRenderState) => React.ReactNode;
	selectedOptions: ReadonlyArray<IOption<TValue, TMetadata>>;
	triggerRef?: React.Ref<HTMLButtonElement>;
}

/**
 * Component for the trigger of the select component.
 * @template TValue - The type of the value of the option.
 * @param {ISelectTriggerProps<TValue>} props - The props of the component.
 * @returns {React.ReactNode} - The trigger component.
 */
export const SelectTrigger = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	className,
	disabled = false,
	error,
	open = false,
	placeholder,
	renderValue,
	selectedOptions,
	triggerRef,
	...props
}: ISelectTriggerProps<TValue, TMetadata>): React.ReactElement => {
	const hasSelectedOptions = selectedOptions.length > 0;
	const valueContent = renderValue
		? renderValue(selectedOptions, { disabled, open, placeholder })
		: selectedOptions.map(option => option.label).join(', ');

	return (
		<button
			{...props}
			className={cn(
				'flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl border-2 border-borderPrimary bg-white px-4 py-2 text-left transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-highlightSelected disabled:cursor-not-allowed disabled:bg-bgFieldsDisabled disabled:opacity-60',
				error && 'border-error',
				className
			)}
			data-testid="select-trigger"
			disabled={disabled}
			ref={triggerRef}
			type="button"
		>
			<span className="min-w-0 flex-1 truncate text-xs text-primary">
				{hasSelectedOptions || renderValue ? (
					typeof valueContent === 'string' ? (
						<CaptionTypography className="text-primary">{valueContent}</CaptionTypography>
					) : (
						valueContent
					)
				) : (
					<CaptionTypography className="text-secondary">{placeholder}</CaptionTypography>
				)}
			</span>
			<ChevronDown
				aria-hidden="true"
				className={cn('h-4 w-4 shrink-0 text-primary transition-transform', open && 'rotate-180')}
			/>
		</button>
	);
};
